import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  Animated,
} from "react-native";
import { useOnboardingTips } from "../../../contexts/OnboardingTipsContext";
import { TOnboardingStep, TTooltipStep } from "../../../utils/constant";
import { Caret } from "../../../assets";

type StickyOnboardingModalProps = {
  steps: TOnboardingStep[];
  autoAdvanceOnMobileStep?: TTooltipStep;
};

const StickyOnboardingModal: React.FC<StickyOnboardingModalProps> = ({
  steps,
  autoAdvanceOnMobileStep,
}) => {
  const { tipsState, markStepComplete } = useOnboardingTips();
  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < 644;

  const [isMinimized, setIsMinimized] = React.useState(false);
  const animWidth = React.useRef(new Animated.Value(300)).current;
  const animHeight = React.useRef(new Animated.Value(160)).current;

  // Auto-complete step if on mobile
  React.useEffect(() => {
    if (!isMobile || !autoAdvanceOnMobileStep) return;
    const step = steps.find((s) => s.step === autoAdvanceOnMobileStep);
    if (step && tipsState[step.step] && !tipsState[step.next]) {
      markStepComplete(step.step);
    }
  }, [isMobile, autoAdvanceOnMobileStep, tipsState]);

  // Find current step
  const currentStep = steps.find(
    ({ step, next }) => tipsState[step] && !tipsState[next],
  );
  if (!currentStep) return null;

  const toggleMinimize = () => {
    Animated.parallel([
      Animated.timing(animWidth, {
        toValue: isMinimized ? 300 : 48,
        duration: 200,
        useNativeDriver: false,
      }),
      Animated.timing(animHeight, {
        toValue: isMinimized ? 160 : 48,
        duration: 200,
        useNativeDriver: false,
      }),
    ]).start();
    setIsMinimized(!isMinimized);
  };

  return (
    <Animated.View
      style={[
        styles.wrapper,
        {
          width: animWidth,
          height: animHeight,
        },
      ]}
    >
      <TouchableOpacity style={styles.caretButton} onPress={toggleMinimize}>
        <Caret direction={isMinimized ? "left" : "right"} />
      </TouchableOpacity>

      {!isMinimized && (
        <View style={styles.contentContainer}>
          <Text style={styles.title}>{currentStep.title}</Text>
          <Text style={styles.text}>{currentStep.text}</Text>
          <TouchableOpacity
            style={styles.button}
            onPress={() => markStepComplete(currentStep.step)}
          >
            <Text style={styles.buttonText}>Got it</Text>
          </TouchableOpacity>
        </View>
      )}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: "absolute",
    bottom: 24,
    right: 16,
    borderRadius: 20,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -3 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 10,
    overflow: "hidden",
    zIndex: 1000,
  },
  caretButton: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    backgroundColor: "#fff",
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 6,
    zIndex: 10,
  },
  contentContainer: {
    marginTop: 32,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    fontFamily: "Urbanist600",
    color: "#222",
    marginBottom: 8,
    textAlign: "center",
  },
  text: {
    fontSize: 15,
    color: "#555",
    fontFamily: "Urbanist500",
    textAlign: "center",
    marginBottom: 10,
    lineHeight: 22,
  },
  button: {
    backgroundColor: "#00B7F4",
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 8,
    width: 150,
    alignItems: "center",
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Urbanist600",
  },
});

export default StickyOnboardingModal;
